import React, { useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import type { GridColDef, GridListViewColDef } from '@mui/x-data-grid-premium';
import ActionsMenuCell from '@/components/common/ActionsMenuCell';
import {
  formatInvestorHomePercentage,
  formatInvestorLastLogin,
  formatInvestorNetWorth,
  type TranslateFn,
} from '@/modules/clientServices/hooks/useClientColumns';
import type { AdvisorInvestor } from '@/store/usersApi';
import { formatNumberToCurrency } from '@/utils/currency';
import { formatAppDate } from '@/utils/date';

export type MenuForInvestorComponent = React.ComponentType<{
  investor: AdvisorInvestor;
  hideTrigger?: boolean;
  size?: 'small' | 'medium' | 'large';
}>;

interface UseDashboardClientCardColumnArgs {
  t: TranslateFn;
  columns: ReadonlyArray<GridColDef<AdvisorInvestor>>;
  openMenuForInvestor: (
    investor: AdvisorInvestor,
    e: React.MouseEvent<HTMLElement>,
  ) => void;
  MenuForInvestor: MenuForInvestorComponent;
  onSelect: (investor: AdvisorInvestor) => void;
}

/**
 * The table columns each side of a card shows, in order. Name and AUM lead
 * their side; the rest render as labeled caption lines under them.
 */
const LEFT_FIELDS = ['Name', 'UserName', 'LastLogin', 'LastOrionLogin'];
const RIGHT_FIELDS = ['Home', 'HomePercentage', 'NetWorth'];

const LEFT_HEADING_FIELD = 'Name';
const RIGHT_HEADING_FIELD = 'Home';

const UNLABELED_FIELDS = new Set(['Name', 'UserName', 'Home']);

const formatCardValue = (
  field: string,
  row: AdvisorInvestor,
  t: TranslateFn,
): string => {
  switch (field) {
    case 'Name':
      return row.Name;
    case 'UserName':
      return row.UserName ?? '';
    case 'NetWorth':
      return formatInvestorNetWorth(row);
    case 'Home':
      return formatNumberToCurrency(row.Home);
    case 'HomePercentage':
      return formatInvestorHomePercentage(row);
    case 'LastLogin':
      return formatInvestorLastLogin(row, t);
    case 'LastOrionLogin':
      return formatAppDate(row.LastOrionLogin, { fallback: '-' });
    default:
      return '-';
  }
};

export function useDashboardClientCardColumn({
  t,
  columns,
  openMenuForInvestor,
  MenuForInvestor,
  onSelect,
}: UseDashboardClientCardColumnArgs): GridListViewColDef<AdvisorInvestor> {
  return useMemo(() => {
    const labels = new Map(
      columns.map((column) => [column.field, column.headerName ?? '']),
    );
    const sideFields = (fields: string[]) =>
      fields.filter((field) => labels.has(field));
    const leftFields = sideFields(LEFT_FIELDS);
    const rightFields = sideFields(RIGHT_FIELDS);

    const renderLine = (
      field: string,
      row: AdvisorInvestor,
      headingField: string,
    ) => {
      const value = formatCardValue(field, row, t);
      if (field === headingField) {
        return (
          <Typography
            key={field}
            variant='subtitle2'
          >
            {value}
          </Typography>
        );
      }
      const label = labels.get(field);
      return (
        <Typography
          key={field}
          variant='caption'
          color='text.secondary'
          component='p'
        >
          {UNLABELED_FIELDS.has(field) || !label ? value : `${label} ${value}`}
        </Typography>
      );
    };

    return {
      field: 'listColumn',
      headerName: '',
      flex: 1,
      minWidth: 0,
      renderCell: ({ row, hasFocus }) => (
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <Box
            role='button'
            tabIndex={hasFocus ? 0 : -1}
            aria-label={row.Name}
            onClick={() => {
              onSelect(row);
            }}
            onKeyDown={(event) => {
              if (event.key !== 'Enter' && event.key !== ' ') return;
              if (event.key === ' ') {
                event.preventDefault();
              }
              onSelect(row);
            }}
            sx={{
              flex: 1,
              minWidth: 0,
              py: 2,
              pl: 3,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 1,
              '&:focus-visible': {
                outline: '2px solid',
                outlineColor: 'primary.main',
                outlineOffset: -2,
              },
            }}
          >
            <Box sx={{ minWidth: 0, overflowWrap: 'anywhere' }}>
              {leftFields.map((field) =>
                renderLine(field, row, LEFT_HEADING_FIELD),
              )}
            </Box>
            <Box sx={{ textAlign: 'right' }}>
              {rightFields.map((field) =>
                renderLine(field, row, RIGHT_HEADING_FIELD),
              )}
            </Box>
          </Box>
          <Box sx={{ pr: 3, flexShrink: 0 }}>
            <ActionsMenuCell
              size='small'
              onClick={(e) => {
                openMenuForInvestor(row, e);
              }}
            />
            <MenuForInvestor
              investor={row}
              hideTrigger
              size='small'
            />
          </Box>
        </Box>
      ),
    };
  }, [t, columns, openMenuForInvestor, MenuForInvestor, onSelect]);
}
